import React, { useState, useEffect } from 'react';
import { ArrowRight, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolveImageUrl } from '../utils/resolveImageUrl';

// Full-width auto-playing YouTube video background, with the editorial content
// (search, CTAs, stats) overlaid. Driven by the admin "Hero Video" settings:
//   heroUseVideo (default on) and heroVideoId (default the Big Chief clip).
const Hero = () => {
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [videoLoaded, setVideoLoaded] = useState(false);

  const eyebrow = settings.heroEyebrow || "Winnipeg’s Trusted Dealership";
  const youtubeVideoId = settings.heroVideoId || 'L8Ln2mEZFkI';
  const useVideoBackground = settings.heroUseVideo !== false;
  const fallbackImage = (settings.heroImage && resolveImageUrl(settings.heroImage))
    || `https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`;

  // Lazy-load the YouTube iframe after first paint; skip during the react-snap
  // prerender (its headless browser never reaches network-idle on a video stream,
  // which would time out the production build).
  useEffect(() => {
    const isPrerender = typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
    if (!useVideoBackground || isPrerender) return;
    const timer = setTimeout(() => setVideoLoaded(true), 1500);
    return () => clearTimeout(timer);
  }, [useVideoBackground]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/inventory?search=${encodeURIComponent(q)}` : '/inventory');
  };

  const renderHeading = () => {
    const title = settings.heroTitle;
    if (title?.includes('Vehicle')) {
      return (<>{title.split('Vehicle')[0]}<span className="text-red-600">Vehicle</span>{title.split('Vehicle')[1]}</>);
    }
    return title || <>Find your perfect <span className="text-red-600">vehicle.</span></>;
  };

  return (
    <section className="relative overflow-hidden bg-zinc-950 min-h-[90vh] flex items-center" data-testid="hero-section">
      {/* Background: auto-playing video (or static image fallback) */}
      <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
        {useVideoBackground ? (
          <>
            {/* Poster frame shown until the video is ready */}
            <img
              src={`https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`}
              alt=""
              aria-hidden="true"
              onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`; }}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
            />
            {videoLoaded && (
              <iframe
                src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=1&loop=1&playlist=${youtubeVideoId}&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&playsinline=1&disablekb=1&fs=0&cc_load_policy=0&vq=hd1080`}
                title="Background video"
                className="absolute border-0 pointer-events-none"
                style={{
                  width: '100vw', height: '56.25vw',
                  minHeight: '100%', minWidth: '177.77vh',
                  top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                frameBorder="0"
                loading="lazy"
              />
            )}
          </>
        ) : (
          <img
            src={fallbackImage}
            alt="Big Chief Motors"
            onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`; }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Readability overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/35"></div>
      </div>

      {/* Content overlaid on the video */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-red-600"></span>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-300">{eyebrow}</span>
          </div>

          <h1 className="font-display font-black text-white leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl drop-shadow-lg" data-testid="hero-title">
            {renderHeading()}
          </h1>

          <p className="mt-6 text-lg text-gray-200 max-w-xl leading-relaxed" data-testid="hero-subtitle">
            {settings.heroSubtitle || "Handpicked trucks, SUVs & cars — transparent pricing, no hidden fees, and financing for every credit situation."}
          </p>

          {/* Quick inventory search */}
          <form onSubmit={handleSearch} className="mt-7 max-w-md" role="search" aria-label="Search inventory">
            <div className="flex items-stretch bg-zinc-900/90 backdrop-blur rounded-full overflow-hidden ring-1 ring-zinc-600 focus-within:ring-2 focus-within:ring-red-500 transition">
              <div className="flex items-center pl-4 text-gray-400"><Search size={20} /></div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search make, model, or keyword…"
                aria-label="Search vehicles"
                className="flex-1 px-3 py-3.5 text-white placeholder-gray-500 outline-none bg-transparent text-sm sm:text-base"
              />
              <button type="submit" className="bg-red-600 hover:bg-red-700 text-white px-6 font-semibold text-sm sm:text-base transition-colors">
                Search
              </button>
            </div>
          </form>

          {/* CTAs */}
          <div className="mt-7 flex flex-wrap gap-4">
            <Link to="/inventory" data-testid="hero-cta-browse">
              <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2 group transition shadow-lg shadow-red-600/20">
                {settings.heroCta1 || "Browse Inventory"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition" />
              </button>
            </Link>
            <Link to="/apply-now" data-testid="hero-cta-finance">
              <button className="border-2 border-white/80 text-white hover:bg-white hover:text-black px-8 py-4 rounded-full font-semibold transition">
                {settings.heroCta2 || "Get Pre-Approved"}
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
            <div data-testid="hero-stat-1">
              <p className="text-3xl font-display font-bold text-white">{settings.stat1Value || "500+"}</p>
              <p className="text-sm text-gray-300 mt-1">{settings.stat1Label || "Happy Customers"}</p>
            </div>
            <div data-testid="hero-stat-2">
              <p className="text-3xl font-display font-bold text-white">{settings.stat2Value || "100%"}</p>
              <p className="text-sm text-gray-300 mt-1">{settings.stat2Label || "No Hidden Fees"}</p>
            </div>
            <div data-testid="hero-stat-3">
              <p className="text-3xl font-display font-bold text-white">{settings.stat3Value || "Fast"}</p>
              <p className="text-sm text-gray-300 mt-1">{settings.stat3Label || "Easy Financing"}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
